import { useEffect } from 'react';

/**
 * Severity levels for error monitoring (mirrors @butter/monitoring LogLevel)
 */
type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

/**
 * Error categories for booking flow errors
 */
type ErrorCategory = 'club_configuration' | 'data_validation' | 'api_error' | 'user_action';

/**
 * Context for error monitoring
 */
export interface ErrorMonitoringContext {
  category: ErrorCategory;
  type: string;
  severity?: LogLevel;
  clubId?: number;
  extra?: Record<string, unknown>;
}

/**
 * Monitoring context passed to captureException / captureMessage callbacks.
 * Intentionally loose so any monitoring provider can satisfy the contract.
 */
export interface MonitoringCaptureContext {
  level?: LogLevel;
  tags?: Record<string, string | number | boolean>;
  extra?: Record<string, unknown>;
}

export interface BookingErrorProps {
  /** Error title shown to user */
  title: string;
  /** Detailed error message shown to user */
  message: string;
  /** Optional Error object to capture */
  error?: Error;
  /** Monitoring context for error tracking */
  context?: ErrorMonitoringContext;
  /**
   * Optional callback to capture an Error with monitoring context.
   * Injected by the consumer so the component has zero monitoring imports.
   *
   * Signature mirrors `captureException` from `@butter/monitoring`.
   */
  onCaptureException?: (error: Error | unknown, context?: MonitoringCaptureContext) => void;
  /**
   * Optional callback to capture a message with monitoring context.
   * Signature mirrors `captureMessage` from `@butter/monitoring`.
   */
  onCaptureMessage?: (message: string, level?: LogLevel, context?: MonitoringCaptureContext) => void;
}

/**
 * BookingError Component
 * 
 * Displays a user-friendly error message and automatically captures
 * the error to monitoring (via injected callbacks) with proper context.
 * 
 * @example Configuration error
 * ```tsx
 * <BookingError
 *   title="Missing pricing configuration"
 *   message="This club is not properly configured for bookings. Please contact support."
 *   onCaptureException={captureException}
 *   onCaptureMessage={captureMessage}
 *   context={{
 *     category: 'club_configuration',
 *     type: 'missing_pricing',
 *     severity: 'error',
 *     clubId: club?.id,
 *     extra: { hasPublicHours: !!club?.publicHours }
 *   }}
 * />
 * ```
 * 
 * @example API error
 * ```tsx
 * <BookingError
 *   title="Unable to load club information"
 *   message={error.message}
 *   error={error}
 *   onCaptureException={captureException}
 *   context={{
 *     category: 'api_error',
 *     type: 'club_fetch_failed',
 *     severity: 'error'
 *   }}
 * />
 * ```
 */
export function BookingError({
  title,
  message,
  error,
  context,
  onCaptureException,
  onCaptureMessage,
}: BookingErrorProps) {
  // Capture error to monitoring on mount (via injected callbacks)
  useEffect(() => {
    if (context) {
      const errorToCapture = error || new Error(title);
      const severity = context.severity || 'error';
      
      const captureCtx: MonitoringCaptureContext = {
        tags: {
          error_category: context.category,
          error_type: context.type,
          ...(context.clubId && { club_id: context.clubId }),
        },
        extra: context.extra,
      };

      // Use captureMessage for warnings (no stack trace needed)
      if (severity === 'warning' || severity === 'info') {
        onCaptureMessage?.(title, severity, captureCtx);
      } else {
        // Use captureException for errors (includes stack trace)
        onCaptureException?.(errorToCapture, { level: severity, ...captureCtx });
      }
    }
  }, [title, error, context, onCaptureException, onCaptureMessage]);
  
  return (
    <>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Make a Booking
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Let's get you set up for your next visit
        </p>
      </div>
      
      {/* Error Alert */}
      <div
        className="rounded-lg border-2 border-red-200 bg-red-50 p-6"
        role="alert"
      >
        <h3 className="mb-2 text-sm font-semibold text-red-800">
          {title}
        </h3>
        <p className="text-sm text-red-700">
          {message}
        </p>
      </div>
    </>
  );
}
